import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';

// Past this much horizontal travel the gesture counts as a swipe rather than a
// tap or a vertical scroll that drifted.
const SWIPE_THRESHOLD_PX = 40;
const DIRECTION_LOCK_PX = 10;

// A press that lands on any of these is the reader operating the step, not
// reaching for the next one.
const INTERACTIVE = 'a, button, input, textarea, select, label, [contenteditable], [role="button"]';

// The track is only ever as tall as the slide in view, so a step that grows
// after mount — an image decoding, an answer coming back — is followed rather
// than clipped.
const SwipeableViews = ({
  index = 0,
  onChangeIndex,
  disabled = false,
  enableMouseEvents = false,
  slideStyle,
  children,
}) => {
  const slides = React.Children.toArray(children);
  const activeRef = useRef(null);
  const gesture = useRef(null);
  const [height, setHeight] = useState();
  const [offset, setOffset] = useState(0);
  // State rather than a ref: the transform's transition is chosen from this
  // during render, so a ref would always describe the previous gesture.
  const [dragging, setDragging] = useState(false);

  // Measured before paint, so the track opens at the height of the step in
  // view rather than the tallest one and then collapsing to it.
  useLayoutEffect(() => {
    const node = activeRef.current;
    if (!node) return;
    const measure = () => setHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [index, slides.length]);

  const begin = (x, y, target) => {
    if (disabled) return;
    if (target && target.closest && target.closest(INTERACTIVE)) return;
    gesture.current = {x, y, dx: 0, axis: null};
  };

  const drag = useCallback((x, y) => {
    const current = gesture.current;
    if (!current) return;
    const dx = x - current.x;
    if (!current.axis) {
      const dy = y - current.y;
      if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return;
      current.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (current.axis === 'x') setDragging(true);
    }
    if (current.axis !== 'x') return;
    current.dx = dx;
    setOffset(dx);
  }, []);

  const finish = useCallback(() => {
    const current = gesture.current;
    gesture.current = null;
    setDragging(false);
    setOffset(0);
    if (!current || Math.abs(current.dx) < SWIPE_THRESHOLD_PX) return;
    // Rechecked here, not just at the start: the step can become one the
    // reader may not leave while the gesture is still in the air.
    if (disabled) return;
    const next = current.dx < 0 ? index + 1 : index - 1;
    if (next >= 0 && next < slides.length && onChangeIndex) onChangeIndex(next);
  }, [disabled, index, slides.length, onChangeIndex]);

  const mouseHandlers = enableMouseEvents ? {
    onMouseDown: event => begin(event.clientX, event.clientY, event.target),
    onMouseMove: event => drag(event.clientX, event.clientY),
    onMouseUp: finish,
    onMouseLeave: finish,
  } : {};

  return (
    <div
      style={{
        overflow: 'hidden',
        height,
        transition: 'height 0.35s ease',
        // Only while a swipe is live, so text stays selectable at rest.
        userSelect: dragging ? 'none' : undefined,
      }}
      onTouchStart={event => begin(event.touches[0].clientX, event.touches[0].clientY, event.target)}
      onTouchMove={event => drag(event.touches[0].clientX, event.touches[0].clientY)}
      onTouchEnd={finish}
      onTouchCancel={finish}
      {...mouseHandlers}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          transform: `translateX(calc(${-index * 100}% + ${offset}px))`,
          transition: dragging ? 'none' : 'transform 0.35s ease',
        }}
      >
        {slides.map((slide, position) => {
          // Hidden rather than merely clipped, so the controls of a step that
          // is off screen are out of the tab order and out of the screen
          // reader. Neighbours come back while a swipe is in flight, because
          // that is the one time they are meant to be seen.
          const revealed = dragging || position === index;

          return (
            <div
              key={position}
              ref={position === index ? activeRef : null}
              aria-hidden={!revealed}
              style={{
                width: '100%',
                flexShrink: 0,
                visibility: revealed ? undefined : 'hidden',
                ...slideStyle,
              }}
            >
              {slide}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SwipeableViews;
